<template>
  <div>
    <template v-for="(submenu, index) in submenus" :key="index">
      <section v-if="isSubmenuVisible(submenu)" class="dashboard-section">
        <header class="section-header">
          <h4 class="section-title">{{ submenu.name }}</h4>
        </header>
        <div class="section-body">
          <div class="row dashboard-card-grid">
            <template v-for="(page, pageIndex) in submenu.pages" :key="getStatItem(page.route)">
              <component
                v-if="page.status === 'active' && (page.card_type === 'input' || page.card_type === 'list' || (statisticsItem && statisticsItem[getStatItem(page.route)]))"
                :is="cards[getComponentKey(page)]"
                :page="page"
                :itemKey="getStatItem(page.route)"
                :statisticsItem="statisticsItem"
                :warehouses="warehouses"
                :productTypeOptions="productTypeOptions"
                :groupByOptions="groupByOptions"
                @onDateChange="onDateChange"
                @onTypeChange="onTypeChange"
                @onWarehouseChange="onWarehouseChange"
                :timeRange="timeRange"
              />
            </template>
          </div>
        </div>
      </section>
    </template>
    
    <div v-if="(!statisticsItem || Object.keys(statisticsItem).length === 0) && !hasInputOrListCards" class="dashboard-empty-state">
      <i class="feather icon-info"></i>
      <h4>Please Sync Dashboard First</h4>
    </div>
  </div>
</template>

<script setup>
import {useRoute} from "vue-router";
import {onMounted, ref, computed} from "vue";
import {useCardStatistics} from "@/services/modules/dashboard/cardStatistics";
import handleBusinessesLocations from "@/services/modules/businessesLocations";
import handleInventory from "@/services/modules/inventory";
import {
  CardTotalType,
  CardBarChart,
  CardPieChart,
  CardSummary,
  CardHosTotal,
  CardHosSummary,
  CardHosBarChart,
  AttendanceList,
  Attendance,
  CardLeaveSchedule,
  CardLeaveApproval,
  CardProjectActivityGroupTva,
  CardProjectActivityTopAchieve,
  CardProjectActivityLowAchieve,
  CardProjectActivityDonorKpi,
  CardTimesheetSummary,
  CardOrderProductionSummary,
  CardProductionTargetActualSummary,
  CardHrWorkforce,
  CardBudgetKeyMetrics,
  CardBudgetTopVariances,
  CardBudgetChart,
  CardBudgetComparison
} from '@/components/molecule/company/dashboard/dynamic-dashboard/components';

const cards = {
  'card_total_value': CardTotalType,
  'card_bar_chart': CardBarChart,
  'card_pie_chart': CardPieChart,
  'card_summary': CardSummary,
  'card_hos_total': CardHosTotal,
  'card_hos_summary': CardHosSummary,
  'card_hos_bar_chart': CardHosBarChart,
  'card_attendance_list': AttendanceList,
  'card_attendance': Attendance,
  'card_leave_schedule': CardLeaveSchedule,
  'card_leave_approval': CardLeaveApproval,
  'card_project_activity_group_tva': CardProjectActivityGroupTva,
  'card_project_activity_top_achieve': CardProjectActivityTopAchieve,
  'card_project_activity_low_achieve': CardProjectActivityLowAchieve,
  'card_project_activity_donor_kpi': CardProjectActivityDonorKpi,
  'card_timesheet_summary': CardTimesheetSummary,
  'card_order_production': CardOrderProductionSummary,
  'card_production_target_actual': CardProductionTargetActualSummary,
  'card_hr_workforce': CardHrWorkforce,
  'card_budget_key_metrics': CardBudgetKeyMetrics,
  'card_budget_top_variances': CardBudgetTopVariances,
  'card_budget_chart': CardBudgetChart,
  'card_budget_comparison': CardBudgetComparison,
};

const props = defineProps({
  submenus: Array,
})

const getComponentKey = (page) => {
  const formattedRoute = getStatItem(page.route);
  if (page.card_type === 'input' || page.card_type === 'list') {
    return "card_" + formattedRoute;
  }
  return statisticsItem.value[formattedRoute].dashboard_type;
};

const hasInputOrListCards = computed(() => {
  return props.submenus.some(submenu => 
    submenu.pages.some(page => 
      page.status === 'active' && (page.card_type === 'input' || page.card_type === 'list')
    )
  )
})

const isSubmenuVisible = (submenu) => {
  return submenu.pages.some(page => 
    page.status === 'active' && (
      page.card_type === 'input' || 
      page.card_type === 'list' || 
      (statisticsItem.value && statisticsItem.value[getStatItem(page.route)])
    )
  )
}

const emit = defineEmits(['updated'])

const extractAndEmitLastUpdated = (data) => {
  if (data) {
    const firstDataKey = Object.keys(data)[0]
    if (firstDataKey && data[firstDataKey] && data[firstDataKey].calculated_at) {
      emit('updated', data[firstDataKey].calculated_at)
    }
  }
}

onMounted(async () => {
  const initialData = await getDashboardSummariesFromCache()
  extractAndEmitLastUpdated(initialData)
  fetchWarehouses()
  fetchOrderProductionOptions()

  // Listen for reload event from sync button
  window.addEventListener('reload-dashboard-summaries', handleReloadSummaries)
})

const handleReloadSummaries = async (event) => {
  const shouldFilter = event.detail && event.detail.company_ids && event.detail.company_ids.length
  let companyIds = [];
  let filterQuery = '';

  if (shouldFilter) {
    const companyIds = event.detail.company_ids.map(company => company.id);

    const params = new URLSearchParams();

    companyIds.forEach(id => {
      params.append('company_ids[]', id);
    });

    filterQuery = `&${params.toString()}`;
  }

  const reloadedData = await getDashboardSummaries(filterQuery, shouldFilter)
  extractAndEmitLastUpdated(reloadedData)
}

const getStatItem = (routeKey) => {
  return routeKey.replace(/-/g, '_')
}

const route = useRoute()
const params = route.params

const onDateChange = (item) => {
  const warehouseQuery = item.warehouse_id ? `&warehouse_id=${item.warehouse_id}` : '';
  const query = `&dashboard_key=${item.dashboard_key}&period_type=${item.period_type}` + warehouseQuery;
  getDashboardSummaryByKey(query, item.dashboard_key, item.warehouse_id);
}

const onTypeChange = (item, type) => {
  const warehouseQuery = item.warehouse_id ? `&warehouse_id=${item.warehouse_id}` : '';
  const query = `&dashboard_key=${item.dashboard_key}&period_type=${item.period_type}&type=${type}&group_by=${type}` + warehouseQuery;
  getDashboardSummaryByKey(query, item.dashboard_key, item.warehouse_id);
};

const warehouses = ref([])
const productTypeOptions = ref([])
const groupByOptions = ref([])

const { fetchBusinessLocationsList } = handleBusinessesLocations()
const { fetchHome } = handleInventory()

const hasWarehouseFilterableCards = () => {
  return Object.values(statisticsItem.value || {}).some(item => item.data && item.data.is_warehouse_filterable)
}

const productionCardTypes = ['card_order_production', 'card_production_target_actual']

const hasOrderProductionCards = () => {
  return Object.values(statisticsItem.value || {}).some(item => productionCardTypes.includes(item.dashboard_type))
}

const toOptionList = (map) => {
  return Object.keys(map || {}).map(function (value) {
    return { value: value, label: map[value] }
  })
}

const fetchWarehouses = () => {
  if (! hasWarehouseFilterableCards()) return

  fetchBusinessLocationsList(`?company_id=${params.companyId}`).then(res => {
    if (! res.data) return
    warehouses.value = [{ id: '', text: 'All Warehouses' }, ...res.data]
  })
}

const fetchOrderProductionOptions = () => {
  if (! hasOrderProductionCards()) return

  fetchHome().then(res => {
    if (! res.status) return
    productTypeOptions.value = toOptionList(res.data.order_production_type)
    groupByOptions.value = toOptionList(res.data.order_production_group_by)
  })
}

const onWarehouseChange = (item, warehouseId) => {
  const query = `&dashboard_key=${item.dashboard_key}&period_type=${item.period_type}&warehouse_id=${warehouseId || ''}`;
  getDashboardSummaryByKey(query, item.dashboard_key, warehouseId);
}

defineExpose({
  onDateChange,
  onTypeChange
});

const {
  timeRange,
  getDashboardSummaries,
  getDashboardSummariesFromCache,
  getDashboardSummaryByKey,
  statisticsItem
} = useCardStatistics(params)
</script>

<style scoped>
.dashboard-section {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(34, 41, 47, 0.06);
  margin-bottom: 28px;
  overflow: hidden;
}

.section-header {
  padding: 18px 22px 14px;
  border-bottom: 1px solid #f0f1f4;
}

.section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #2b2b2b;
}

.section-body {
  padding: 22px;
}

.dashboard-empty-state {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(34, 41, 47, 0.06);
  padding: 60px 20px;
  text-align: center;
  margin-top: 8px;
  width: 100%;
}

.dashboard-empty-state i {
  font-size: 28px;
  color: #b7b7b7;
  display: block;
  margin-bottom: 10px;
}

.dashboard-empty-state h4 {
  color: #8a8a8a;
  margin: 0;
  font-size: 15px;
}

:deep(.dashboard-card-grid .card .cut-text) {
  width: 143px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
